import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import Map from "../assets/images/image.webp";

const FaqHeroSection = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
      className="relative w-full min-h-[30vh] lg:min-h-[50vh] xl:min-h-[60vh] flex justify-center items-center text-white text-center bg-cover bg-center bg-no-repeat p-4"
      style={{
        backgroundImage:
          "linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://images.unsplash.com/photo-1497366811353-6870744d04b2?ixlib=rb-4.0.3&auto=format&fit=crop&w=2069&q=80')",
      }}
    >
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.8 }}
        className="max-w-4xl w-full px-4 lg:px-8"
      >
        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl mb-4 font-bold">
          FAQ
        </h1>
        <p className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl max-w-prose mx-auto">
          To establish ourselves as respected engineering
          consultant, offering comprehensive and innovative solutions. We are
          committed to unmatched performance, unparalleled accountability and
          exceptional client satisfaction.
        </p>
      </motion.div>
    </motion.div>
  );
};

const FAQSection = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  const faqs = [
    {
      "question": "What services do you offer?",
      "answer": "We provide engineering and project management solutions tailored to client needs."
    },
    {
      "question": "How can I contact your team?",
      "answer": "You can reach out to us via email or phone, available on our contact page."
    },
    {
      "question": "What industries do you specialize in?",
      "answer": "We specialize in infrastructure, energy and sustainability projects."
    },
    {
      "question": "Do you offer customized solutions?",
      "answer": "Yes, we tailor our solutions to meet the specific requirements of our clients."
    },
    {
      "question": "What is your approach to project planning?",
      "answer": "We follow a structured methodology that includes feasibility analysis, design, implementation and quality assurance to ensure project success."
    },
    {
      "question": "Do you provide ongoing support after project completion?",
      "answer": "Yes, we offer post-project support, maintenance and consultation to ensure long-term success."
    }
];


  return (
    <div className="p-4 lg:p-8 text-black flex-1">
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-2xl sm:text-3xl lg:text-4xl mb-4 text-center font-bold"
      >
        Frequently Asked Questions
      </motion.h2>
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="text-sm sm:text-base lg:text-lg mb-6 max-w-prose mx-auto"
      >
        We provide high-quality engineering and project management solutions,
        focusing on safety, efficiency and cost-effectiveness. Built on
        integrity and innovation, we strive to create lasting value for our
        clients and industry.
      </motion.p>
      <div className="max-w-2xl mx-auto">
        {faqs.map((faq, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="mb-2"
          >
            <motion.div
              className="flex justify-between items-center p-3 sm:p-4 bg-white border-b border-gray-200 cursor-pointer"
              onClick={() => setActiveIndex(activeIndex === index ? null : index)}
              whileHover={{ scale: 1.01 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
            >
              <span className="text-sm sm:text-base lg:text-lg">
                {faq.question}
              </span>
              <motion.span
                animate={{ rotate: activeIndex === index ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                ∨
              </motion.span>
            </motion.div>
            <AnimatePresence>
              {activeIndex === index && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="p-3 sm:p-4 bg-gray-100 rounded overflow-hidden"
                >
                  <p className="text-sm sm:text-base lg:text-lg">
                    {faq.answer}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

const CombinedSection = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="flex flex-wrap gap-4 p-4 lg:p-8 items-start justify-center max-w-[90rem] mx-auto"
    >
      <motion.div
        initial={{ x: -50, opacity: 0 }}
        whileInView={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="flex flex-col gap-4 w-full sm:w-60 lg:w-72 xl:w-80"
      >
        <motion.div
          whileHover={{ scale: 1.05 }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
          className="h-40 sm:h-48 lg:h-56 xl:h-64 rounded-2xl overflow-hidden"
        >
          <img
            src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
            alt="Engineering consultation meeting"
            className="w-full h-full object-cover"
          />
        </motion.div>
        <motion.div
          whileHover={{ scale: 1.05 }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
          className="h-auto py-4 bg-[rgb(143,31,49)] text-center px-4 text-white flex flex-col justify-center items-center rounded-2xl gap-2"
        >
          <h6 className="text-sm sm:text-base lg:text-lg">
            Need More About Us
          </h6>
          <Link to="/contact">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            className="border border-white px-3 py-2 rounded-full text-sm sm:text-base hover:bg-white hover:text-[rgb(143,31,49)] transition-colors"
          >
            Start a Project
          </motion.button></Link>
        </motion.div>
      </motion.div>
      <motion.div
        initial={{ x: 50, opacity: 0 }}
        whileInView={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        whileHover={{ scale: 1.05 }}
        className="h-48 sm:h-56 lg:h-64 xl:h-72 w-full sm:w-60 lg:w-72 xl:w-80 rounded-2xl overflow-hidden"
      >
        <img
          src="https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
          alt="Engineering project blueprint"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="flex-1 min-w-[300px] w-full lg:w-auto">
        <FAQSection />
      </div>
    </motion.div>
  );
};

const StartProjectSection = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 1 }}
      className="w-full min-h-[30vh] lg:min-h-[40vh] xl:min-h-[50vh] flex justify-center items-center text-white text-center bg-cover bg-center bg-no-repeat p-4"
      style={{
        backgroundImage:
          "linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://images.unsplash.com/photo-1541888946425-d81bb19240f5?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')",
      }}
    >
      <motion.div
        initial={{ y: 50, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.8 }}
        className="max-w-4xl w-full px-4"
      >
        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl mb-6">
          Building today for a sustainable tomorrow.
        </h1>
        
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="border-2 border-white px-4 py-2 sm:px-6 sm:py-3 rounded-full text-sm sm:text-base lg:text-lg hover:bg-white hover:text-black transition-colors"
        >
          <Link to="/contact">
         Start A Project</Link>
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

const GetInTouch = () => {
  const [formData, setFormData] = useState({
    name: "",
    contactNumber: "",
    email: "",
    altPhone: "",
    organisation: "",
    query: "",
  });

  const [status, setStatus] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch("https://usebasin.com/f/a4f7a8a64e49", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus("Message sent successfully!");
        setFormData({
          name: "",
          contactNumber: "",
          email: "",
          altPhone: "",
          organisation: "",
          query: "",
        });
      } else {
        setStatus("Failed to send message. Try again later.");
      }
    } catch (error) {
      setStatus("Something went wrong.");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="flex flex-wrap gap-8 p-4 lg:p-8 justify-center max-w-[90rem] mx-auto"
    >
      <motion.div
        initial={{ x: -50, opacity: 0 }}
        whileInView={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        whileHover={{ scale: 1.02 }}
        className="w-full lg:w-auto lg:flex-1 max-w-md"
      >
        <img
          src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
          alt="Modern architectural building"
          className="w-full h-[300px] sm:h-[400px] lg:h-[600px] xl:h-[720px] object-cover rounded-lg"
        />
      </motion.div>
      <motion.div
        initial={{ x: 50, opacity: 0 }}
        whileInView={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="flex flex-col gap-4 w-full lg:w-auto lg:flex-1 max-w-lg"
      >
        <motion.h2
          initial={{ y: 20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="text-2xl sm:text-3xl lg:text-4xl font-bold"
        >
          Have a question? Get in touch
        </motion.h2>
        <motion.p
          initial={{ y: 20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-sm sm:text-base lg:text-lg"
        >
          At Ray Infrastructures, we believe in building not just structures,
          but lasting legacies, combining cutting-edge technology, sustainable
          practices, and unparalleled craftsmanship to create spaces that stand
          the test of time and meet the evolving needs of the future.
        </motion.p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <motion.input
            whileFocus={{ scale: 1.02 }}
            type="text"
            name="name"
            placeholder="Name"
            value={formData.name}
            onChange={handleChange}
            required
            className="p-2 sm:p-3 rounded-lg text-sm sm:text-base bg-gray-100 w-full"
          />
          
          <motion.input
            whileFocus={{ scale: 1.02 }}
            type="tel"
            name="contactNumber"
            placeholder="Contact Number"
            value={formData.contactNumber}
            onChange={handleChange}
            required
            className="p-2 sm:p-3 rounded-lg text-sm sm:text-base bg-gray-100 w-full"
          />

          <motion.input
            whileFocus={{ scale: 1.02 }}
            type="email"
            name="email"
            placeholder="Email Address"
            value={formData.email}
            onChange={handleChange}
            required
            className="p-2 sm:p-3 rounded-lg text-sm sm:text-base bg-gray-100 w-full"
          />

          <motion.input
            whileFocus={{ scale: 1.02 }}
            type="tel"
            name="altPhone"
            placeholder="Alternative Phone Number"
            value={formData.altPhone}
            onChange={handleChange}
            required
            className="p-2 sm:p-3 rounded-lg text-sm sm:text-base bg-gray-100 w-full"
          />

          <motion.input
            whileFocus={{ scale: 1.02 }}
            type="text"
            name="organisation"
            placeholder="Organisation"
            value={formData.organisation}
            onChange={handleChange}
            className="p-2 sm:p-3 rounded-lg text-sm sm:text-base bg-gray-100 w-full"
          />

          <motion.textarea
            whileFocus={{ scale: 1.02 }}
            name="query"
            placeholder="Describe your query here"
            value={formData.query}
            onChange={handleChange}
            required
            rows="4"
            className="p-2 sm:p-3 rounded-lg text-sm sm:text-base bg-gray-100 w-full"
          />

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            type="submit"
            className="bg-[rgb(143,31,49)] text-white p-2 sm:p-3 rounded-full text-sm sm:text-base w-full hover:bg-[rgb(163,51,69)] transition-colors"
          >
            Send Message
          </motion.button>

          {status && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center text-sm sm:text-base mt-2"
            >
              {status}
            </motion.p>
          )}
        </form>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-4 w-full h-32 sm:h-40 lg:h-48"
        >
          <a href="https://maps.app.goo.gl/JucKizmPN2Xganqi9">
          <img
            src={Map}
            alt="Ray Infrastructures Location Map"
            className="w-full h-full object-cover rounded-lg"
          />
          </a>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

const FAQ = () => {
  return (
    <div className="w-full">
      <FaqHeroSection />
      <CombinedSection />
      <StartProjectSection />
      <GetInTouch />
    </div>
  );
};

export default FAQ;